/* ═══════════════════════════════════════════════════════════════════════════
   BkForm — the new/edit booking form. Extracted from App.jsx (2026-05-29).
   Pure relocation; behaviour unchanged. See
   docs/superpowers/specs/2026-05-29-app-jsx-decomposition-design.md
   ═══════════════════════════════════════════════════════════════════════════ */
import { memo, useState, useEffect, useRef } from "react";
import {
  isoToday, isoMake, nightCount, overlap, cleanPhone, getRate,
  refTotal, refNightly, bcomCalc, cityTaxCalc, breakfastCost, getMinStay, extraBedCost,
} from './utils';
import { CL, FONT } from './theme.js';
import { extraLabel, displaySource, isSofaBedExtra } from './labels.js';
import { Diamond } from './icons.jsx';

const BkForm = memo(function BkForm({ initial, rooms, allBk, gDir, guestNotes, guestTrusted, onSave, onCancel, showToast, rName, t, lang, commissionPct = 15, extras = [], sources: propSources, trackOtaCommission = true, cur = '€', property = null }) {
  const sources = (propSources && propSources.length > 0) ? propSources : (lang === 'bg' ? ['Директно'] : ['Direct']);
  const [f, setF] = useState(() => {
    // Clamp the seeded source to one that actually exists in the property's
    // sources list. If startNew defaulted to "Direct" but the owner trimmed
    // their sources to just ["Booking.com", "Phone"], we'd render an empty
    // <select> that visually shows the first option — which isn't what the
    // user picked. Prefer Direct → Phone → first non-Booking.com → first.
    let source = initial.source;
    if (source && !sources.includes(source)) {
      source =
        sources.find(s => /^(direct|директно)$/i.test(s)) ||
        sources.find(s => /^(phone|телефон)$/i.test(s)) ||
        sources.find(s => s !== 'Booking.com') ||
        sources[0];
    }
    return {
      ...initial,
      source: source || initial.source,
      lang: initial.lang || (cleanPhone(initial.phone || '').startsWith('359') ? 'bg' : 'en'),
      langTouched: !!initial.lang,
    };
  });
  const [matched, setMatched] = useState(null);
  const [sugg, setSugg] = useState([]);
  const [pFoc, setPFoc] = useState(false);
  const [priceEdited, setPE] = useState(false);
  const isEdit = allBk.some(b => b.id === f.id);

  useEffect(() => {
    const cl = cleanPhone(f.phone);
    if (cl.length < 4) { setMatched(null); setSugg([]); return; }
    const ms = Object.entries(gDir).filter(([k]) => k.includes(cl));
    if (ms.length === 1 && ms[0][0] === cl) { setMatched(ms[0][1]); setSugg([]); }
    else { setMatched(null); setSugg(ms.slice(0, 5)); }
  }, [f.phone, gDir]);

  // Auto-detect booking lang from phone country code until user manually overrides.
  useEffect(() => {
    if (f.langTouched) return;
    const cl = cleanPhone(f.phone);
    if (cl.length < 4) return;
    const detected = cl.startsWith('359') ? 'bg' : 'en';
    if (detected !== f.lang) setF(prev => ({ ...prev, lang: detected }));
  }, [f.phone, f.langTouched]);

  function applyGuest(g) { setF(p => ({ ...p, guestName: g.name, phone: g.phone, email: g.email || p.email })); setMatched(g); setSugg([]); }
  function up(patch) { setF(prev => ({ ...prev, ...patch })); }

  const nc = nightCount(f.checkIn, f.checkOut);
  const selRoom = rooms.find(r => r.id === f.roomId);
  const rn = selRoom ? refNightly(selRoom, f.checkIn, f.checkOut) : 0;
  const rt = selRoom ? refTotal(selRoom, f.checkIn, f.checkOut) : 0;

  function isFree(rid, ci, co) {
    return !allBk.some(b =>
      b.roomId === rid && b.id !== f.id
      && b.status !== 'cancelled' && b.status !== 'no_show'
      && overlap(ci, co, b.checkIn, b.checkOut)
    );
  }
  // Hide archived rooms from new selections, but keep them visible if this booking already references one
  // (so editing an existing booking on an archived room still shows the room).
  const avail = (f.checkIn && f.checkOut)
    ? rooms.filter(r => isFree(r.id, f.checkIn, f.checkOut) && (!r.archived || r.id === f.roomId))
    : rooms.filter(r => !r.archived || r.id === f.roomId);

  // Price is locked at booking time. Auto-fill from room rates only when the user actually
  // changes the room/dates/source — never on mount, even if room rates have changed since.
  const prevRoomSig = useRef({ roomId: f.roomId, checkIn: f.checkIn, checkOut: f.checkOut, source: f.source });
  useEffect(() => {
    const newSig = { roomId: f.roomId, checkIn: f.checkIn, checkOut: f.checkOut, source: f.source };
    const changed = newSig.roomId !== prevRoomSig.current.roomId
      || newSig.checkIn !== prevRoomSig.current.checkIn
      || newSig.checkOut !== prevRoomSig.current.checkOut
      || newSig.source !== prevRoomSig.current.source;
    prevRoomSig.current = newSig;
    if (!changed) return;
    if (selRoom && nc > 0 && !priceEdited) {
      const total = refTotal(selRoom, f.checkIn, f.checkOut);
      up({ price: String(total), pricePerNight: String(Math.round(total / nc)) });
    }
  }, [f.roomId, f.checkIn, f.checkOut, f.source]);

  // Snapshot is locked at booking time. Recompute only when the user actually changes
  // party/dates/breakfast — not on mount or when property settings change.
  const prevPartySig = useRef({
    adults: f.adults,
    childrenSig: JSON.stringify(f.children),
    checkIn: f.checkIn,
    checkOut: f.checkOut,
    breakfastIncluded: f.breakfastIncluded,
  });
  useEffect(() => {
    const newSig = {
      adults: f.adults,
      childrenSig: JSON.stringify(f.children),
      checkIn: f.checkIn,
      checkOut: f.checkOut,
      breakfastIncluded: f.breakfastIncluded,
    };
    const changed = newSig.adults !== prevPartySig.current.adults
      || newSig.childrenSig !== prevPartySig.current.childrenSig
      || newSig.checkIn !== prevPartySig.current.checkIn
      || newSig.checkOut !== prevPartySig.current.checkOut
      || newSig.breakfastIncluded !== prevPartySig.current.breakfastIncluded;
    prevPartySig.current = newSig;
    if (!changed) return;
    if (!f.breakfastIncluded || !property?.breakfast_enabled) return;
    if (property.breakfast_pricing_mode !== 'per_person' && property.breakfast_pricing_mode !== 'per_room') return;
    const nights = nightCount(f.checkIn, f.checkOut);
    const party = { adults: f.adults || 0, children: Array.isArray(f.children) ? f.children : [] };
    const newSnap = breakfastCost(property, party, nights).total;
    const oldSnap = Number(f.breakfastPrice) || 0;
    if (newSnap === oldSnap) return;
    const oldTotal = Number(f.price) || 0;
    up({ breakfastPrice: newSnap, price: String(Math.max(0, oldTotal - oldSnap + newSnap)) });
  }, [f.adults, f.children, f.checkIn, f.checkOut, f.breakfastIncluded]);

  useEffect(() => {
    if (!selRoom || nc <= 0) return;
    const ebCount = Number(f.extraBeds) || 0;
    const { total: ebCost } = extraBedCost(selRoom.type || '', ebCount, property, nc);
    setF(prev => {
      const baseTotal = (Number(prev.pricePerNight) || 0) * nc;
      const bfCost = prev.breakfastIncluded ? (Number(prev.breakfastPrice) || 0) : 0;
      const newTotal = baseTotal + ebCost + bfCost;
      if (newTotal === (Number(prev.price) || 0)) return prev;
      return { ...prev, price: String(newTotal) };
    });
  }, [f.extraBeds, f.roomId, f.checkIn, f.checkOut]);

  function onNightlyChange(v) { const ppn = Number(v) || 0; up({ pricePerNight: v, price: nc > 0 ? String(ppn * nc) : v }); setPE(true); }
  function onTotalChange(v) { const tot = Number(v) || 0; up({ price: v, pricePerNight: nc > 0 ? String(Math.round(tot / nc)) : v }); setPE(true); }

  // Source change — when user selects Booking.com AND the property has bcom_paid_via_ota
  // turned on (default), auto-mark Paid + clear deposit. If the hotel collects payment on
  // arrival from Bcom guests (toggle off in Settings → OTA), don't override payment status.
  function onSourceChange(s) {
    const bcomAutoPaid = property?.bcom_paid_via_ota !== false;
    if (s === 'Booking.com' && bcomAutoPaid) up({ source: s, paymentStatus: 'Paid', depositAmount: '' });
    else up({ source: s });
  }

  const isBcom = f.source === "Booking.com";
  const { minStay: roomMinStay, label: minStayLabel } = (selRoom && f.checkIn)
    ? getMinStay(selRoom, f.checkIn)
    : { minStay: 1, label: null };
  const showMinStayAdvisory = !isBcom && nc > 0 && roomMinStay > 1;
  const gross = Number(f.price) || 0;
  const vccFeePct = property?.bcom_vcc_fee_pct ?? 1.5;
  const comm = (isBcom && trackOtaCommission) ? bcomCalc(gross, commissionPct, vccFeePct) : null;
  const cityTax = (isBcom && property?.city_tax_rate > 0) ? cityTaxCalc(property, f) : 0;

  function save() {
    if (!f.guestName.trim()) { showToast(t.enterNm); return; }
    const cp = cleanPhone(f.phone);
    if (!cp || cp.length < 5) { showToast(t.phoneReq); return; }
    if (!f.roomId) { showToast(t.selRm); return; }
    if (!f.checkIn || !f.checkOut) { showToast(t.setDt); return; }
    if (new Date(f.checkOut + "T12:00:00") <= new Date(f.checkIn + "T12:00:00")) { showToast(t.coAfter); return; }
    if (!isFree(f.roomId, f.checkIn, f.checkOut)) { showToast(t.rmNA); return; }
    if (f.paymentStatus === "Deposit" && !f.depositAmount) { showToast(t.depositAmtReq); return; }
    if (f.breakfastIncluded && (Number(f.breakfastPrice) || 0) < 0) { showToast(t.breakfastNegPrice); return; }
    const finalBkIncluded = property?.breakfast_pricing_mode === 'included' ? true : !!f.breakfastIncluded;
    const finalBkPrice = property?.breakfast_pricing_mode === 'included' ? 0 : (Number(f.breakfastPrice) || 0);
    // Single-note model: f.note carries the booking note (migration 012 added
    // bookings.note + bookings.lang). f.langTouched is local-only state — strip
    // it before save.
    const { langTouched, ...persisted } = f;
    onSave({ ...persisted, breakfastIncluded: finalBkIncluded, breakfastPrice: finalBkPrice });
  }

  // ── Inline calendar state (replaces the separate DatePicker card; matches the locked mockup's Stay section) ──
  const [calVm, setCalVm] = useState(() => {
    if (f.checkIn) { const d = new Date(f.checkIn + "T12:00:00"); return [d.getFullYear(), d.getMonth()]; }
    return [new Date().getFullYear(), new Date().getMonth()];
  });
  const [vY, vM] = calVm;
  const dim = new Date(vY, vM + 1, 0).getDate();
  const fdow = (new Date(vY, vM, 1).getDay() + 6) % 7;
  const todayStr = isoToday(new Date());
  function tapDay(ds) {
    if (!f.checkIn || (f.checkIn && f.checkOut)) { up({ checkIn: ds, checkOut: '' }); setPE(false); }
    else if (ds <= f.checkIn) { up({ checkIn: ds, checkOut: '' }); setPE(false); }
    else { up({ checkOut: ds }); setPE(false); }
  }

  // ── Local style atoms (mockup-aligned: 40px inputs, hairline-divided sections, polished pill groups) ──
  const sec = { padding: '14px 18px' };
  const secDiv = { borderTop: `1px solid ${CL.goldL}` };
  const secH = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 };
  const secTitle = { fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body };
  const lbl = { display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, marginBottom: 6, fontFamily: FONT.body };
  const inp = { width: '100%', padding: '10px 12px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9, fontSize: 14, fontFamily: FONT.body, background: '#FFFEF9', outline: 'none', color: CL.bark, height: 40, boxSizing: 'border-box', colorScheme: 'light' };
  const fieldGap = { marginTop: 14 };
  const pillGrp = { display: 'inline-flex', border: `1px solid ${CL.goldL}`, borderRadius: 999, overflow: 'hidden', background: CL.cream, height: 28 };
  const pillBtn = (on) => ({
    background: on ? CL.bark : 'transparent', border: 'none', padding: '0 12px',
    fontSize: 11, fontWeight: 600, color: on ? CL.gold : CL.stone, letterSpacing: 0.5,
    textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT.body,
    display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1, height: '100%',
  });
  const seg = { display: 'inline-flex', border: `1px solid ${CL.goldL}`, borderRadius: 6, overflow: 'hidden', background: CL.cream, height: 24 };
  const segBtn = (on) => ({
    background: on ? CL.bark : 'transparent', border: 'none', padding: '0 10px',
    fontSize: 11, fontWeight: 700, color: on ? CL.gold : CL.stone, letterSpacing: 0.6,
    cursor: 'pointer', fontFamily: FONT.body,
  });
  function stepBtn(disabled = false) {
    return {
      width: 30, height: 30, border: `1px solid ${CL.goldL}`, borderRadius: 8,
      background: CL.cream, color: CL.bark, fontSize: 16, fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: 0,
      fontFamily: FONT.body, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    };
  }
  const stepVal = { fontFamily: FONT.display, fontSize: 18, fontWeight: 500, minWidth: 18, textAlign: 'center', color: CL.bark, fontVariantNumeric: 'lining-nums tabular-nums' };

  const channels = [
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'viber', label: 'Viber' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: lang === 'bg' ? 'Телефон' : 'Phone' },
  ];
  const dowAbbr = lang === 'bg' ? ['П','В','С','Ч','П','С','Н'] : ['M','T','W','T','F','S','S'];

  return (
    <div style={{ background: CL.sand, minHeight: '100svh' }}>
      {/* Top bar — sand, ✕ left, Cormorant title centred */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 12px', background: CL.sand, position: 'sticky', top: 0, zIndex: 50 }}>
        <button onClick={onCancel} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 18, color: CL.bark, width: 28, textAlign: 'left', fontWeight: 300, lineHeight: 1 }}>✕</button>
        <span style={{ fontFamily: FONT.display, fontSize: 15, letterSpacing: 1, color: CL.bark }}>{isEdit ? t.editBk : t.newBk}</span>
        <span style={{ width: 28 }} />
      </div>

      {/* Single cream surface — hairline-divided sections */}
      <div style={{ background: CL.cream, margin: '10px 14px', borderRadius: 14, border: `1px solid ${CL.goldL}`, overflow: 'hidden' }}>

        {/* ◆ GUEST */}
        <div style={sec}>
          <div style={secH}><Diamond size={5} /><span style={secTitle}>{lang === 'bg' ? 'Гост' : 'Guest'}</span></div>

          <div>
            <label style={lbl}>{t.phone} *</label>
            <input style={{ ...inp, borderColor: matched ? CL.olive : CL.goldL }} placeholder="+359 88 123 4567" type="tel" value={f.phone}
              onChange={e => up({ phone: e.target.value })} onFocus={() => setPFoc(true)} onBlur={() => setTimeout(() => setPFoc(false), 250)} />

            {matched && (
              <div style={{ marginTop: 8, background: '#F4F8EE', border: `1px solid ${CL.olive}`, borderRadius: 10, padding: '9px 12px', fontSize: 12, color: '#2D5A1E', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ color: CL.gold, fontSize: 14, lineHeight: 1.2 }}>★</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700 }}>{matched.name}</span> · {matched.stays.length} {t.stays} · {cur}{matched.totalSpend.toLocaleString()}
                  {matched.roomIds.length > 0 && (
                    <div style={{ fontSize: 11, color: CL.olive, marginTop: 1 }}>{t.favRm}: {matched.roomIds.map(rName).join(', ')}</div>
                  )}
                </div>
              </div>
            )}

            {matched && guestNotes[cleanPhone(matched.phone)] && (
              <div style={{ marginTop: 6, background: '#FFF8E1', border: '1px solid #E8D5B0', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: '#8B6914' }}>
                📝 {guestNotes[cleanPhone(matched.phone)]}
              </div>
            )}

            {sugg.length > 0 && pFoc && (
              <div style={{ marginTop: 6, background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '6px 12px', background: CL.sand, fontSize: 11, fontWeight: 700, color: CL.stone }}>{t.matchG}</div>
                {sugg.map(([k, g]) => (
                  <button key={k} onClick={() => applyGuest(g)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '8px 14px', border: 'none', borderBottom: `1px solid ${CL.sand}`, background: CL.cream, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <div><div style={{ fontSize: 14, fontWeight: 600, color: CL.bark }}>{g.name}</div><div style={{ fontSize: 12, color: CL.stone }}>{g.phone}</div></div>
                    <div style={{ fontSize: 12, color: CL.olive, fontWeight: 600 }}>{g.stays.length} {t.stays}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={fieldGap}>
            <label style={lbl}>{t.name} *</label>
            <input style={inp} value={f.guestName} onChange={e => up({ guestName: e.target.value })} />
          </div>

          <div style={fieldGap}>
            <label style={lbl}>{t.email}</label>
            <input style={inp} type="email" value={f.email || ''} onChange={e => up({ email: e.target.value })} />
          </div>

          {/* Preferred contact — 4 pill group */}
          <div style={fieldGap}>
            <label style={lbl}>{lang === 'bg' ? 'Предпочитан контакт' : 'Preferred contact'}</label>
            <span style={pillGrp}>
              {channels.map(ch => {
                const on = f.preferredContact === ch.key;
                return (
                  <button key={ch.key} onClick={() => up({ preferredContact: on ? null : ch.key })} style={pillBtn(on)}>{ch.label}</button>
                );
              })}
            </span>
          </div>

          {/* Lang segment + auto-from-phone hint */}
          <div style={{ ...fieldGap, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ ...lbl, margin: 0 }}>{lang === 'bg' ? 'Език' : 'Lang'}</span>
            <span style={seg}>
              <button onClick={() => setF(p => ({ ...p, lang: 'en', langTouched: true }))} style={segBtn(f.lang === 'en')}>EN</button>
              <button onClick={() => setF(p => ({ ...p, lang: 'bg', langTouched: true }))} style={segBtn(f.lang === 'bg')}>BG</button>
            </span>
            {!f.langTouched && cleanPhone(f.phone).startsWith('359') && (
              <span style={{ marginLeft: 'auto', fontSize: 10.5, color: CL.gold, fontWeight: 600, letterSpacing: 0.4 }}>
                {lang === 'bg' ? 'авто от +359' : 'auto from +359'}
              </span>
            )}
          </div>
        </div>

        {/* ◆ STAY — inline calendar */}
        <div style={{ ...sec, ...secDiv }}>
          <div style={secH}><Diamond size={5} /><span style={secTitle}>{lang === 'bg' ? 'Престой' : 'Stay'}</span></div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 10px' }}>
            <button onClick={() => setCalVm(vM === 0 ? [vY - 1, 11] : [vY, vM - 1])} style={{ width: 24, height: 24, border: 'none', background: 'none', color: CL.bark, fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
            <span style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark, fontVariantNumeric: 'lining-nums tabular-nums' }}>{t.months[vM]} {vY}</span>
            <button onClick={() => setCalVm(vM === 11 ? [vY + 1, 0] : [vY, vM + 1])} style={{ width: 24, height: 24, border: 'none', background: 'none', color: CL.bark, fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {dowAbbr.map((d, i) => (
              <div key={i} style={{ fontSize: 9, textAlign: 'center', color: CL.stone, fontWeight: 700, letterSpacing: 1.4, padding: '4px 0', textTransform: 'uppercase', fontFamily: FONT.body }}>{d}</div>
            ))}
            {Array(fdow).fill(null).map((_, i) => <div key={'e' + i} />)}
            {Array.from({ length: dim }, (_, i) => {
              const day = i + 1;
              const ds = isoMake(vY, vM + 1, day);
              const past = ds < todayStr;
              const isCi = ds === f.checkIn;
              const isCo = ds === f.checkOut;
              const inR = f.checkIn && f.checkOut && ds > f.checkIn && ds < f.checkOut;
              const isToday = ds === todayStr;
              let bg = 'transparent', cl = past ? '#B8860B' : CL.bark, br = '6px', outline = 'none';
              if (isCi) { bg = CL.olive; cl = '#fff'; br = '6px 0 0 6px'; }
              else if (isCo) { bg = CL.olive; cl = '#fff'; br = '0 6px 6px 0'; }
              else if (inR) { bg = '#EFE7D2'; cl = CL.bark; br = '0'; }
              if (isToday && !isCi && !isCo) outline = `1px solid ${CL.gold}`;
              return (
                <button key={day} onClick={() => tapDay(ds)} style={{
                  height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: bg, color: cl, border: 'none', borderRadius: br,
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  fontFamily: FONT.display, opacity: past ? 0.6 : 1, outline, outlineOffset: -1,
                  fontVariantNumeric: 'lining-nums tabular-nums', padding: 0,
                }}>{day}</button>
              );
            })}
          </div>

          {f.checkIn && (() => {
            const locale = lang === 'bg' ? 'bg-BG' : 'en-GB';
            const fmtPretty = (ds) => new Date(ds + 'T12:00:00').toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: CL.stone, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${CL.goldL}` }}>
                <span>
                  <strong style={{ color: CL.bark, fontWeight: 600, fontFamily: FONT.body }}>{fmtPretty(f.checkIn)}</strong>
                  {f.checkOut && ' → '}
                  {f.checkOut && <strong style={{ color: CL.bark, fontWeight: 600, fontFamily: FONT.body }}>{fmtPretty(f.checkOut)}</strong>}
                </span>
                {f.checkOut && <span><strong style={{ color: CL.bark, fontWeight: 600, fontFamily: FONT.body }}>{nc}</strong> {nc === 1 ? t.nt : t.nts}</span>}
              </div>
            );
          })()}

          {f.checkIn && f.checkIn < todayStr && (
            <div style={{ marginTop: 8, background: '#FFF8E1', border: '1px solid #B8860B', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: '#8B6914', fontWeight: 600 }}>
              ⚠️ {t.pastDateWarn}
            </div>
          )}

          {showMinStayAdvisory && (
            <div style={{ marginTop: 8, fontSize: 12, color: CL.stone }}>
              {lang === 'bg'
                ? `Минимален престой${minStayLabel ? ` (${minStayLabel})` : ''}: ${roomMinStay} нощи`
                : `Min stay${minStayLabel ? ` (${minStayLabel})` : ''}: ${roomMinStay} nights`}
            </div>
          )}
        </div>

        {/* ◆ ROOM */}
        <div style={{ ...sec, ...secDiv }}>
          <div style={secH}>
            <Diamond size={5} />
            <span style={secTitle}>{lang === 'bg' ? 'Стая' : 'Room'}</span>
            {f.checkIn && f.checkOut && (
              <span style={{ color: CL.olive, fontSize: 10.5, fontWeight: 600, marginLeft: 4, fontFamily: FONT.body }}>{avail.length} {t.avail}</span>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {avail.map(r => {
              const fav = matched?.roomIds?.includes(r.id);
              const sel = f.roomId === r.id;
              const sr = f.checkIn ? getRate(r, f.checkIn) : r.basePrice;
              const typeMatch = f._requestedType ? r.type === f._requestedType : null;
              const dimmed = typeMatch === false && !sel;
              return (
                <button key={r.id} onClick={() => { up({ roomId: r.id }); setPE(false); }} style={{
                  padding: '8px 11px', borderRadius: 10, fontSize: 11.5, cursor: 'pointer',
                  textAlign: 'left', minWidth: 78, position: 'relative',
                  border: sel ? `1.5px solid ${CL.bark}` : (typeMatch || fav) ? `1.5px solid ${CL.olive}` : `1.5px solid ${CL.goldL}`,
                  background: sel ? CL.bark : typeMatch ? '#F1F8E9' : CL.cream,
                  color: sel ? CL.gold : CL.bark,
                  opacity: dimmed ? 0.4 : 1,
                  textDecoration: dimmed ? 'line-through' : 'none',
                  fontFamily: FONT.body, lineHeight: 1.2,
                }}>
                  {fav && !typeMatch && <span style={{ position: 'absolute', top: -6, right: -4, fontSize: 14 }}>⭐</span>}
                  <span style={{ display: 'block', fontWeight: 600, fontSize: 13 }}>{r.name}</span>
                  <span style={{ display: 'block', fontSize: 10, fontWeight: 500, color: sel ? CL.goldL : CL.stone, marginTop: 1 }}>{r.type} · {cur}{sr}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ◆ PARTY */}
        <div style={{ ...sec, ...secDiv }}>
          <div style={secH}><Diamond size={5} /><span style={secTitle}>{lang === 'bg' ? 'Гости' : 'Party'}</span></div>

          {(() => {
            const childCount = Array.isArray(f.children) ? f.children.length : (Number(f.children) || 0);
            const cap = selRoom ? selRoom.capacity : 99;
            const maxA = selRoom ? Math.min(cap - childCount, selRoom.maxAdults ?? cap) : 99;
            const maxC = selRoom ? Math.min(cap - f.adults, selRoom.maxChildren ?? cap) : 99;
            return (
              <div style={{ display: 'flex', gap: 18, alignItems: 'flex-end' }}>
                <div>
                  <label style={lbl}>{t.adults}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => up({ adults: Math.max(1, f.adults - 1) })} style={stepBtn()}>−</button>
                    <span style={stepVal}>{f.adults}</span>
                    <button onClick={() => { if (f.adults < maxA) up({ adults: f.adults + 1 }); }} style={stepBtn(f.adults >= maxA)}>+</button>
                  </div>
                </div>
                <div>
                  <label style={lbl}>{t.children}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => { const ch = Array.isArray(f.children) ? f.children : []; if (ch.length > 0) up({ children: ch.slice(0, -1) }); }} style={stepBtn()}>−</button>
                    <span style={stepVal}>{childCount}</span>
                    <button onClick={() => { if (childCount < maxC) { const ch = Array.isArray(f.children) ? f.children : []; up({ children: [...ch, { age: '' }] }); } }} style={stepBtn(childCount >= maxC)}>+</button>
                  </div>
                </div>
              </div>
            );
          })()}

          {Array.isArray(f.children) && f.children.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {f.children.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ ...lbl, margin: 0 }}>{lang === 'bg' ? `Дете ${i + 1} години` : `Child ${i + 1} age`}</span>
                  <input style={{ ...inp, width: 80, height: 32, padding: '6px 10px', fontSize: 13 }} type="number" inputMode="numeric" min="0" max="17" value={c.age || ''} onChange={e => { const ch = f.children.map((x, j) => j === i ? { ...x, age: e.target.value } : x); up({ children: ch }); }} />
                </div>
              ))}
            </div>
          )}

          {/* Extra-bed stepper — only when no Sofa-bed extra is configured */}
          {(() => {
            if (!selRoom) return null;
            const { pricePerNight: ebPrice } = extraBedCost(selRoom.type || '', 0, property, 1);
            if (!ebPrice) return null;
            if ((extras || []).some(ex => isSofaBedExtra(ex))) return null;
            const maxEb = property?.max_extra_beds?.[selRoom.type] ?? Infinity;
            const ebCount = Number(f.extraBeds) || 0;
            return (
              <div style={{ marginTop: 14 }}>
                <label style={lbl}>{t.extraBeds}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => up({ extraBeds: Math.max(0, ebCount - 1) })} style={stepBtn()}>−</button>
                  <span style={stepVal}>{ebCount}</span>
                  <button onClick={() => up({ extraBeds: Math.min(ebCount + 1, maxEb) })} style={stepBtn(ebCount >= maxEb)}>+</button>
                  <span style={{ fontSize: 12, color: CL.stone, marginLeft: 4 }}>{cur}{ebPrice}/{t.perNight}</span>
                </div>
                {nc > 0 && ebCount > 0 && (
                  <div style={{ fontSize: 11, color: CL.stone, marginTop: 6 }}>
                    {cur}{ebPrice} × {ebCount} × {nc} {nc > 1 ? t.nts : t.nt} = {cur}{ebPrice * ebCount * nc}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ◆ PRICE */}
        <div style={{ ...sec, ...secDiv }}>
          <div style={secH}><Diamond size={5} /><span style={secTitle}>{lang === 'bg' ? 'Цена' : 'Price'}</span></div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>{t.priceN}</label>
              <input style={inp} type="number" inputMode="numeric" placeholder="0" value={f.pricePerNight || ''} onChange={e => onNightlyChange(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>{t.priceT}</label>
              <input style={inp} type="number" inputMode="numeric" placeholder="0" value={f.price || ''} onChange={e => onTotalChange(e.target.value)} />
            </div>
          </div>
          {!isBcom && selRoom && nc > 0 && (() => {
            const ppn = Number(f.pricePerNight) || 0;
            if (!ppn) return null;
            const nts = nc > 1 ? t.nts : t.nt;
            const baseTotal = ppn * nc;

            // Collect per-night chargeable line items beyond the base rate
            const extraLines = [];

            // Extra beds / sofa bed
            const ebCount = Number(f.extraBeds) || 0;
            if (ebCount > 0) {
              const { pricePerNight: ebPpn } = extraBedCost(selRoom.type || '', ebCount, property, nc);
              if (ebPpn) {
                const sofaEx = (property?.extras ?? []).find(isSofaBedExtra);
                const label = sofaEx ? extraLabel(sofaEx, lang) : (lang === 'bg' ? 'Допълнително легло' : 'Extra bed');
                extraLines.push({ label, ppn: ebPpn, count: ebCount, total: ebPpn * ebCount * nc });
              }
            }

            // Breakfast
            const bfCost = f.breakfastIncluded ? (Number(f.breakfastPrice) || 0) : 0;
            if (bfCost > 0) {
              extraLines.push({ label: t.breakfast, ppn: null, count: null, total: bfCost });
            }

            const grandTotal = baseTotal + extraLines.reduce((s, l) => s + l.total, 0);
            const rowStyle = { display: 'flex', justifyContent: 'flex-end', gap: 6, fontSize: 11, color: CL.stone };

            return (
              <div style={{ marginTop: 8, fontStyle: 'italic' }}>
                <div style={rowStyle}>
                  <span>{lang === 'bg' ? 'Цена на нощ' : 'Rate per night'}:</span>
                  <span>{cur}{ppn} × {nc} {nts} = {cur}{baseTotal}</span>
                </div>
                {extraLines.map((l, idx) => (
                  <div key={idx} style={rowStyle}>
                    <span>{l.label}:</span>
                    <span>
                      {l.ppn !== null
                        ? `${cur}${l.ppn} × ${l.count} × ${nc} ${nts} = ${cur}${l.total}`
                        : `${cur}${l.total}`}
                    </span>
                  </div>
                ))}
                {extraLines.length > 0 && (
                  <div style={{ ...rowStyle, fontWeight: 700, color: CL.bark, borderTop: `1px solid ${CL.goldL}`, marginTop: 4, paddingTop: 4 }}>
                    <span>{lang === 'bg' ? 'Общо' : 'Total'}:</span>
                    <span>{[baseTotal, ...extraLines.map(l => l.total)].map(v => `${cur}${v}`).join(' + ')} = {cur}{grandTotal}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {isBcom && (
            <div style={{ fontSize: 12, color: CL.stone, marginTop: 4 }}>
              {lang === 'bg'
                ? 'Въведете цената от потвърждението на Booking.com.'
                : 'Enter the price from your Booking.com confirmation.'}
            </div>
          )}

          {/* Source dropdown — turns sea-tinted on Booking.com */}
          <div style={fieldGap}>
            <label style={lbl}>{t.source}</label>
            <div style={{ position: 'relative' }}>
              <select value={f.source} onChange={e => onSourceChange(e.target.value)}
                style={{
                  ...inp,
                  appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                  cursor: 'pointer', paddingRight: 32,
                  ...(isBcom ? { background: CL.sea, color: '#fff', borderColor: CL.sea } : {}),
                }}>
                {sources.map(s => <option key={s} value={s} style={{ color: CL.bark, background: '#fff' }}>{displaySource(s, lang)}</option>)}
              </select>
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: isBcom ? 'rgba(255,255,255,0.85)' : CL.stone, fontSize: 14 }}>▾</span>
            </div>
          </div>

          {/* Payment 3-pill group with deposit amount inline in active Deposit pill */}
          <div style={fieldGap}>
            <label style={lbl}>{t.payment}</label>
            <span style={{ ...pillGrp, display: 'flex', height: 32, opacity: isBcom ? 0.7 : 1 }}>
              {['Unpaid', 'Deposit', 'Paid'].map(p => {
                const on = f.paymentStatus === p;
                const showAmt = on && p === 'Deposit' && !!f.depositAmount;
                return (
                  <button key={p} onClick={() => { if (!isBcom) up({ paymentStatus: p }); }} disabled={isBcom} style={{
                    ...pillBtn(on),
                    flex: 1, justifyContent: 'center',
                    cursor: isBcom ? 'default' : 'pointer',
                  }}>
                    {t[p.toLowerCase()] || p}
                    {showAmt && (
                      <span style={{
                        marginLeft: 8, paddingLeft: 8, borderLeft: '1px solid rgba(255,255,255,0.25)',
                        fontFamily: FONT.display, fontSize: 13, fontWeight: 500, color: '#fff',
                        textTransform: 'none', letterSpacing: 0,
                        fontVariantNumeric: 'lining-nums tabular-nums',
                      }}>{cur}{f.depositAmount}</span>
                    )}
                  </button>
                );
              })}
            </span>
            {f.paymentStatus === 'Deposit' && !isBcom && (
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>{t.depositAmt} *</label>
                <input style={inp} type="number" inputMode="numeric" placeholder="0" value={f.depositAmount || ''} onChange={e => up({ depositAmount: e.target.value })} />
              </div>
            )}
          </div>

          {/* Booking.com commission card */}
          {isBcom && gross > 0 && comm && (
            <div style={{ marginTop: 14, background: '#E8EFF6', border: `1px solid ${CL.sea}`, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: CL.sea, marginBottom: 4 }}>{t.bcomNote}</div>
              <div style={{ fontSize: 12, color: CL.sea, lineHeight: 1.6 }}>
                {t.gross}: <strong>{cur}{gross}</strong><br />
                {t.commission} ({commissionPct}%): <strong>({cur}{comm.comm})</strong><br />
                {vccFeePct > 0 && <>{t.vccFee} ({vccFeePct}%): <strong>({cur}{comm.vcc})</strong><br /></>}
                <span style={{ fontSize: 13, fontWeight: 700 }}>{t.net}: {cur}{comm.net}</span>
              </div>
              {cityTax > 0 && (
                <div style={{ fontSize: 11, color: CL.stone, marginTop: 6, borderTop: '1px solid #C9D8E8', paddingTop: 6 }}>
                  {lang === 'bg'
                    ? `Туристически данък (събира се при настаняване): ${cur}${cityTax}`
                    : `City tax (collected at check-in, remitted to municipality): ${cur}${cityTax}`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ◆ EXTRAS & NOTE */}
        <div style={{ ...sec, ...secDiv }}>
          <div style={secH}><Diamond size={5} /><span style={secTitle}>{lang === 'bg' ? 'Екстри и бележка' : 'Extras & note'}</span></div>

          {extras && extras.length > 0 && (
            <>
              <label style={lbl}>{t.extras}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                {extras.map((extra, idx) => {
                  const label = extraLabel(extra, lang);
                  if (!label) return null;
                  const sel = (f.extras || []).includes(label);
                  return (
                    <button key={idx} onClick={() => {
                      const curExtras = f.extras || [];
                      const newExtras = sel ? curExtras.filter(x => x !== label) : [...curExtras, label];
                      const updates = { extras: newExtras };
                      if (isSofaBedExtra(extra)) {
                        const maxEb = selRoom ? (property?.max_extra_beds?.[selRoom.type] ?? 1) : 1;
                        updates.extraBeds = sel ? Math.max(0, (Number(f.extraBeds) || 0) - 1) : Math.min((Number(f.extraBeds) || 0) + 1, maxEb);
                      }
                      up(updates);
                    }} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                      letterSpacing: 0.3, cursor: 'pointer', fontFamily: FONT.body,
                      border: sel ? `1px solid ${CL.bark}` : `1px solid ${CL.goldL}`,
                      background: sel ? CL.bark : CL.cream, color: sel ? CL.gold : CL.bark,
                    }}>{label}</button>
                  );
                })}
              </div>
            </>
          )}

          {property?.breakfast_enabled && (() => {
            const mode = property.breakfast_pricing_mode;
            const nights = nightCount(f.checkIn, f.checkOut);
            const party = { adults: f.adults || 0, children: Array.isArray(f.children) ? f.children : [] };

            if (mode === 'included') {
              return (
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>{t.breakfast}</label>
                  <div style={{ fontSize: 13, color: CL.bark }}>{t.breakfastIncludedStatic}</div>
                </div>
              );
            }

            if ((mode === 'none' || !mode) && f.breakfastIncluded) {
              return (
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>{t.breakfast}</label>
                  <div style={{ fontSize: 12, color: CL.stone, fontStyle: 'italic' }}>
                    {t.breakfastLockedSnapshot(cur, f.breakfastPrice || 0)}
                  </div>
                </div>
              );
            }

            if (mode !== 'per_person' && mode !== 'per_room') return null;

            const liveBk = breakfastCost(property, party, nights);
            const unitPrice = Number(property.breakfast_price_per_person) || 0;

            function onAdd() {
              if (f.breakfastIncluded) return;
              const snap = breakfastCost(property, party, nights).total;
              const oldTotal = Number(f.price) || 0;
              up({ breakfastIncluded: true, breakfastPrice: snap, price: String(oldTotal + snap) });
            }
            function onSkip() {
              if (!f.breakfastIncluded) return;
              const oldSnap = Number(f.breakfastPrice) || 0;
              const oldTotal = Number(f.price) || 0;
              up({ breakfastIncluded: false, breakfastPrice: 0, price: String(Math.max(0, oldTotal - oldSnap)) });
            }

            const storedSnap = Number(f.breakfastPrice) || 0;
            const isLocked = !!f.breakfastIncluded && storedSnap > 0 && storedSnap !== liveBk.total;
            const mathLine = isLocked
              ? t.breakfastLockedTotal(cur, storedSnap)
              : (mode === 'per_person'
                  ? t.breakfastPerPersonMath(cur, unitPrice, liveBk.eligibleCount, nights, liveBk.total)
                  : t.breakfastPerRoomMath(cur, unitPrice, nights, liveBk.total));
            const hint = isLocked
              ? t.breakfastLockedHint(cur, unitPrice, mode, liveBk.total)
              : (mode === 'per_person'
                  ? t.breakfastEligibilityHint(party.adults, property.children_max_age)
                  : null);

            return (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>{t.breakfast}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={pillGrp}>
                    <button onClick={onAdd} style={pillBtn(!!f.breakfastIncluded)}>{lang === 'bg' ? 'Добави' : 'Add'}</button>
                    <button onClick={onSkip} style={pillBtn(!f.breakfastIncluded)}>{lang === 'bg' ? 'Пропусни' : 'Skip'}</button>
                  </span>
                  <span style={{ fontSize: 11, color: CL.stone }}>{f.breakfastIncluded ? `+${mathLine}` : mathLine}</span>
                </div>
                {hint && <div style={{ fontSize: 11, color: CL.stone, marginTop: 4 }}>{hint}</div>}
              </div>
            );
          })()}

          <label style={lbl}>{lang === 'bg' ? 'Бележка' : 'Note'}</label>
          <textarea style={{ ...inp, height: 'auto', minHeight: 62, resize: 'vertical', padding: '10px 12px' }}
            placeholder={lang === 'bg' ? 'напр. късно пристигане, алергии…' : 'e.g. late arrival, allergies…'}
            value={f.note || ''}
            onChange={e => up({ note: e.target.value })} />
        </div>
      </div>

      {/* Save footer — flow positioning (matches the locked mockup; sticky causes overlap on long forms) */}
      <div style={{ padding: '12px 14px 16px', background: CL.sand, borderTop: `1px solid ${CL.goldL}`, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
        <button onClick={save} style={{ width: '100%', background: CL.olive, color: '#fff', border: 'none', padding: 14, borderRadius: 11, fontFamily: FONT.body, fontWeight: 600, fontSize: 14.5, letterSpacing: 0.4, cursor: 'pointer' }}>
          {lang === 'bg' ? (isEdit ? 'Запази промените' : 'Запази резервацията') : (isEdit ? 'Save changes' : 'Save booking')}
        </button>
      </div>
    </div>
  );
});

export default BkForm;
